/* import __COLOCATED_TEMPLATE__ from './list.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { get, requestHeaders, ajaxDelete } from 'embercom/lib/ajax';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type NotificationsService from 'embercom/services/notifications-service';
import { hasFeature } from 'embercom/helpers/has-feature';
import type Token from 'embercom/models/custom-authentication/token';

interface Signature {
  Args: {
    customAuthenticationTokens: Token[];
  };
}

export default class List extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @tracked customAuthenticationTokens = this.args.customAuthenticationTokens;
  @tracked tokenToDelete: Token | null = null;
  @tracked showDeleteModal = false;

  get tableColumns() {
    return [
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.name'),
        valuePath: 'name',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.type'),
        valuePath: 'tokenType',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.matched-by-domain'),
        valuePath: 'domain',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.created-by'),
        valuePath: 'person.createdBy',
        type: 'avatar-with-text',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.updated-by'),
        valuePath: 'person.updatedBy',
        type: 'avatar-with-text',
      },
      {
        label: '',
        valuePath: 'actions',
      },
    ].filter((column) => {
      if (column.valuePath === 'domain') {
        return hasFeature('domain-based-token-selection', this.appService);
      }
      return true;
    });
  }

  @action
  async connectToSalesforce(isSandbox: boolean) {
    try {
      let url = `/ember/custom_authentication/oauth/salesforce/connect?app_id=${this.appService.app.id}&sandbox=${isSandbox}`;
      let response = await get(url);

      let form = document.createElement('form');
      form.method = 'POST';
      form.action = response.authorization_url;

      let csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = requestHeaders()['X-CSRF-Token'];
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('settings.custom-authentication-tokens.salesforce.connect-error'),
      });
    }
  }

  @action
  async connectToHubspot() {
    try {
      let url = `/ember/custom_authentication/oauth/hubspot/connect?app_id=${this.appService.app.id}`;
      let response = await get(url);

      let form = document.createElement('form');
      form.method = 'POST';
      form.action = response.authorization_url;

      let csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = requestHeaders()['X-CSRF-Token'];
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('settings.custom-authentication-tokens.hubspot.connect-error'),
      });
    }
  }

  @action
  openDeleteModal(token: Token) {
    this.tokenToDelete = token;
    this.showDeleteModal = true;
  }

  @action
  closeDeleteModal() {
    this.tokenToDelete = null;
    this.showDeleteModal = false;
  }

  @action
  async deleteToken() {
    if (!this.tokenToDelete) {
      return;
    }

    try {
      await ajaxDelete(`/ember/custom_authentication/tokens/${this.tokenToDelete.id}`, {
        app_id: this.appService.app.id,
      });

      this.customAuthenticationTokens = this.customAuthenticationTokens.filter(
        (token: Token) => token.id !== this.tokenToDelete?.id,
      );

      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.custom-authentication-tokens.delete-modal.success-message'),
      );

      this.closeDeleteModal();
    } catch (e: unknown) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t('settings.custom-authentication-tokens.delete-modal.error-message'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::AppSettings::Authentication::List': typeof List;
    'new-settings/app-settings/authentication/list': typeof List;
  }
}
